import {
  EncumbranceExcelGroup,
  EncumbranceExcelRow,
  EncumbrancesExcelValidationError,
} from '../types/EncumbrancesExcel';

const pushError = (
  errors: EncumbrancesExcelValidationError[],
  error: EncumbrancesExcelValidationError
) => {
  errors.push(error);
};

const isSameDate = (
  left: Date | null,
  right: Date | null
): boolean => {
  if (!left && !right) return true;
  if (!left || !right) return false;
  return left.getTime() === right.getTime();
};

const validateGroupConsistency = (
  encumbranceId: string,
  rows: EncumbranceExcelRow[]
): EncumbrancesExcelValidationError[] => {
  const errors: EncumbrancesExcelValidationError[] = [];
  const [firstRow, ...restRows] = rows;

  restRows.forEach((row) => {
    const isConsistent =
      row.type === firstRow.type &&
      row.shareFrom === firstRow.shareFrom &&
      row.shareTo === firstRow.shareTo &&
      row.totalShares === firstRow.totalShares &&
      row.holderCif === firstRow.holderCif &&
      row.holderName === firstRow.holderName &&
      row.date.getTime() === firstRow.date.getTime() &&
      isSameDate(row.endDate, firstRow.endDate);

    if (!isConsistent) {
      pushError(errors, {
        code: 'INCONSISTENT_GROUP_DATA',
        encumbranceId,
        rowIndex: row.rowIndex,
        cif: row.holderCif,
      });
    }
  });

  return errors;
};

const validateGroupParties = (
  encumbranceId: string,
  type: string,
  partyRows: EncumbranceExcelRow[]
): EncumbrancesExcelValidationError[] => {
  const errors: EncumbrancesExcelValidationError[] = [];
  const coOwners = partyRows.filter((row) => row.role === 'CO_OWNER');
  const usufructuaries = partyRows.filter((row) => row.role === 'USUFRUCTUARY');

  if (type === 'CO_OWNERSHIP' && coOwners.length === 0) {
    pushError(errors, { code: 'MISSING_CO_OWNERS', encumbranceId });
  }

  if (type === 'USUFRUCT' && usufructuaries.length === 0) {
    pushError(errors, { code: 'MISSING_USUFRUCTUARY', encumbranceId });
  }

  if (type === 'CO_OWNERSHIP') {
    const percentageSum = coOwners.reduce(
      (sum, row) => sum + (row.percentage || 0),
      0
    );

    if (percentageSum > 100) {
      pushError(errors, {
        code: 'PERCENTAGE_SUM_EXCEEDED',
        encumbranceId,
        detail: String(percentageSum),
      });
    }
  }

  return errors;
};

export const groupEncumbranceExcelById = (
  rows: EncumbranceExcelRow[]
): EncumbranceExcelGroup[] => {
  const grouped = new Map<string, EncumbranceExcelRow[]>();

  rows.forEach((row) => {
    const current = grouped.get(row.encumbranceId) || [];
    current.push(row);
    grouped.set(row.encumbranceId, current);
  });

  const groups: EncumbranceExcelGroup[] = [];

  grouped.forEach((groupRows, encumbranceId) => {
    const errors: EncumbrancesExcelValidationError[] = [];
    const holderRows = groupRows.filter((row) => row.role === 'HOLDER');
    const partyRows = groupRows.filter((row) => row.role !== 'HOLDER');

    if (holderRows.length === 0) {
      pushError(errors, { code: 'MISSING_HOLDER', encumbranceId });
    }

    if (holderRows.length > 1) {
      holderRows.forEach((row) => {
        pushError(errors, {
          code: 'DUPLICATE_HOLDER',
          encumbranceId,
          rowIndex: row.rowIndex,
          cif: row.holderCif,
        });
      });
    }

    errors.push(...validateGroupConsistency(encumbranceId, groupRows));

    const holderRow = holderRows[0] || null;

    if (holderRow) {
      errors.push(
        ...validateGroupParties(encumbranceId, holderRow.type, partyRows)
      );
    }

    groups.push({
      encumbranceId,
      type: holderRow?.type || groupRows[0]?.type || '',
      shareFrom: holderRow?.shareFrom || groupRows[0]?.shareFrom || 0,
      shareTo: holderRow?.shareTo || groupRows[0]?.shareTo || 0,
      totalShares: holderRow?.totalShares || groupRows[0]?.totalShares || 0,
      date: holderRow?.date || groupRows[0]?.date || new Date(0),
      endDate: holderRow?.endDate ?? groupRows[0]?.endDate ?? null,
      holderCif: holderRow?.holderCif || groupRows[0]?.holderCif || '',
      holderName: holderRow?.holderName || groupRows[0]?.holderName || '',
      rows: groupRows,
      holderRow,
      partyRows,
      errors,
      isValid: errors.length === 0,
    });
  });

  return groups.sort((left, right) =>
    left.encumbranceId.localeCompare(right.encumbranceId)
  );
};
